import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Scale, X, Plus } from "lucide-react";
import { useShop } from "../store/ShopContext";
import { api, formatPrice, CATEGORY_NAMES } from "../lib/api";
import { PageHeader } from "../components/PageHeader";

export default function Compare() {
  const { compare, toggleCompare, addToCart, clearCompare } = useShop();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    Promise.all(compare.map((c) => api.get(`/products/${c.slug}`).then((r) => r.data.product).catch(() => null))).then((l) => setProducts(l.filter(Boolean)));
  }, [compare]);

  const rows = useMemo(() => {
    const keys = [];
    products.forEach((p) => p.specs.forEach(([k]) => !keys.includes(k) && keys.push(k)));
    return keys;
  }, [products]);

  const empties = Math.max(0, 3 - products.length);

  return (
    <div data-testid="compare-page">
      <PageHeader eyebrow="Comparare" title="Compară modelele" description="Până la 3 produse, unul lângă altul, cu toate specificațiile tehnice." crumbs={[{ label: "Comparare" }]} testId="compare-header" />
      <div className="container-cc">
        {products.length === 0 ? (
          <div data-testid="compare-empty" className="surface py-24 text-center">
            <Scale size={36} strokeWidth={1} className="mx-auto text-[#B4A38E] mb-5" />
            <p className="font-serif text-3xl">Niciun produs de comparat</p>
            <p className="text-sm text-[#737373] mt-2 max-w-sm mx-auto">Folosește butonul „Compară” de pe produse pentru a le vedea față în față.</p>
            <Link to="/catalog" className="btn-primary mt-8">Mergi la catalog</Link>
          </div>
        ) : (
          <>
            <div className="flex justify-end mb-4"><button data-testid="compare-clear" onClick={clearCompare} className="text-xs uppercase tracking-[0.14em] underline underline-offset-4">Golește lista</button></div>
            <div className="overflow-x-auto no-scrollbar -mx-5 px-5">
              <table data-testid="compare-table" className="w-full min-w-[720px] border-collapse">
                <thead>
                  <tr>
                    <th className="w-44 align-bottom text-left pb-6"><span className="eyebrow">Produs</span></th>
                    {products.map((p) => (
                      <th key={p.slug} data-testid={`compare-col-${p.slug}`} className="align-bottom text-left pb-6 pr-6">
                        <div className="relative surface aspect-[4/5] flex items-center justify-center p-6 mb-4">
                          <img src={p.images[0]} alt={p.name} className="product-img max-h-full object-contain" />
                          <button data-testid={`compare-remove-${p.slug}`} aria-label="Elimină" onClick={() => toggleCompare(p)} className="absolute top-3 right-3 h-8 w-8 bg-white flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white transition-colors"><X size={14} /></button>
                        </div>
                        <p className="eyebrow">{CATEGORY_NAMES[p.category]}</p>
                        <Link to={`/produs/${p.slug}`} className="font-serif text-2xl font-normal block leading-tight mt-1">{p.name}</Link>
                        {p.color && <p className="text-xs text-[#737373] font-normal">{p.color}</p>}
                        <p className="font-semibold mt-2">{formatPrice(p.price)}</p>
                        <button data-testid={`compare-add-to-cart-${p.slug}`} onClick={() => addToCart(p, p.color)} className="btn-primary h-10 px-4 text-[11px] mt-3">Adaugă în coș</button>
                      </th>
                    ))}
                    {Array.from({ length: empties }).map((_, i) => (
                      <th key={i} className="align-bottom pb-6 pr-6">
                        <Link to="/catalog" className="border border-dashed border-black/20 aspect-[4/5] flex flex-col items-center justify-center text-[#737373] hover:border-[#0A0A0A] hover:text-[#0A0A0A] transition-colors">
                          <Plus size={20} strokeWidth={1.2} /><span className="text-[11px] uppercase tracking-[0.16em] mt-2 font-semibold">Adaugă produs</span>
                        </Link>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-black/10"><td className="py-3 text-sm text-[#737373]">Colecție</td>{products.map((p) => <td key={p.slug} className="py-3 text-sm font-medium pr-6">{p.collection}</td>)}{Array.from({ length: empties }).map((_, i) => <td key={i} />)}</tr>
                  {rows.map((k) => (
                    <tr key={k} className="border-t border-black/5">
                      <td className="py-3 text-sm text-[#737373] align-top">{k}</td>
                      {products.map((p) => {
                        const v = p.specs.find(([kk]) => kk === k)?.[1];
                        return <td key={p.slug} className={`py-3 text-sm pr-6 align-top ${v ? "font-medium" : "text-[#C0C0C0]"}`}>{v || "—"}</td>;
                      })}
                      {Array.from({ length: empties }).map((_, i) => <td key={i} />)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
